import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import React, { useReducer } from "react";
import TWItemizedTooltip from "../../../../componentLibrary/TWTooltip/TWItemizedTooltip";
import { StaffDashboardOrgPreviewFragment, useEditOrgMutation, useOrgHasAmsTargetUrlQuery } from "../../../../types";
import { OrgForm } from "../Form/Create";
import { editOrgFormReducer, editOrgFormToSaveTooltips, initialEditOrgFormState, integrationInput } from "../Form/utils";

interface OrgModalProps {
  open: boolean;
  onClose: () => void;
  org: StaffDashboardOrgPreviewFragment;
}

const EditDialog = ({ open, onClose, org }: OrgModalProps) => {
  const { setMessage } = useSnackbar();
  const { data, loading, error } = useOrgHasAmsTargetUrlQuery({
    variables: { id: org.id },
    skip: !open,
    onError: e => setMessage("error", e.message),
  });

  if (!open) {
    return null;
  }

  if (loading) {
    return (
      <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
        <DialogTitle title={`Edit Org`} onClose={onClose} />
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </DialogContent>
      </Dialog>
    );
  }

  // Fail closed: if the AMS lookup errors or is missing, treat as no target URL.
  return (
    <EditDialogContent
      key={org.id}
      org={org}
      hasAmsTargetUrl={error || !data ? false : data.org.hasAmsTargetUrl}
      onClose={onClose}
    />
  );
};

interface EditDialogContentProps {
  org: StaffDashboardOrgPreviewFragment;
  hasAmsTargetUrl: boolean;
  onClose: () => void;
}

const EditDialogContent = ({ org, hasAmsTargetUrl, onClose }: EditDialogContentProps) => {
  const { setMessage } = useSnackbar();
  const [state, dispatch] = useReducer(editOrgFormReducer, initialEditOrgFormState(org, hasAmsTargetUrl));
  const canSaveTooltips = editOrgFormToSaveTooltips(state);

  const [editOrg] = useEditOrgMutation({
    onError: e => setMessage("error", e.message),
  });

  const handleSave = () => {
    if (canSaveTooltips.length) {
      return;
    }
    const {
      allowsPublicAccess,
      isActive,
      isAxleSyncEnabled,
      name,
      compassId,
      allowCompassId,
      cbordId,
      allowCbordId,
      bonAppetitId,
      allowBonAppetitId,
      sodexoId,
      allowSodexoId,
      agreementForm,
      scoringSystem,
      localeCode,
      territoryName,
      amsEnergyExpenditureFormId,
    } = state;
    editOrg({
      variables: {
        input: {
          id: org.id,
          teamworksId: org.teamworksId,
          isActive,
          name,
          allowsPublicAccess,
          isAxleSyncEnabled,
          createMemberships: [],
          editMemberships: [],
          integrations: integrationInput({
            compassId,
            cbordId,
            bonAppetitId,
            sodexoId,
            allowCompassId,
            allowCbordId,
            allowBonAppetitId,
            allowSodexoId,
          }),
          agreementFormId: agreementForm?.id || null,
          scoringSystemId: scoringSystem?.id || null,
          localeCode,
          territoryName,
          amsEnergyExpenditureFormId: amsEnergyExpenditureFormId ? Number(amsEnergyExpenditureFormId) : null,
        },
      },
    });
    onClose();
  };

  return (
    <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
      <DialogTitle title={`Edit Org`} onClose={onClose} />
      <DialogContent>
        <OrgForm state={state} dispatch={dispatch} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <TWItemizedTooltip title="Fix the following before finishing:" items={canSaveTooltips}>
          <Button onClick={handleSave}>Save</Button>
        </TWItemizedTooltip>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
